// CcrUploadPage — /ccr-upload/:token. PUBLIC, owner-facing.
//
// The DEFERRED, low-friction CC&R-collection surface. After an owner intake completes
// with a NEW/unlisted HOA, we email the owner a tokenized link here to upload their
// HOA's CC&Rs later (the intake itself never prompts for the document — that jams the
// process). The token binds the ONE newly-created HOA server-side; the owner just
// picks a single PDF and we take it from there.
//
// Standalone branded surface (NOT wrapped in the authenticated AppShell — see
// App.tsx), talking only to the public, token-gated /api/ccr-upload/:token endpoints
// via lib/ccrUploadClient.ts (no Bearer token).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getCcrUpload,
  uploadCcr,
  CcrUploadApiError,
  type CcrUploadView,
} from '@/lib/ccrUploadClient';

// ---------------------------------------------------------------------------
// Brand header (shared lockup with the other public form pages, DS tokens)
// ---------------------------------------------------------------------------

function BrandLockup({ propertyAddress }: { propertyAddress: string | null }): React.JSX.Element {
  return (
    <header style={{ textAlign: 'center', marginBottom: 'var(--space-7)' }}>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 26,
          lineHeight: 1.2,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--fg)',
        }}
      >
        Welcome&nbsp;Home
      </div>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 13,
          lineHeight: 1.2,
          letterSpacing: '0.42em',
          textTransform: 'uppercase',
          color: 'var(--accent)',
          paddingLeft: '0.42em',
        }}
      >
        Properties
      </div>
      <div
        style={{
          width: 56,
          height: 1,
          backgroundColor: 'var(--accent)',
          margin: 'var(--space-4) auto 0',
        }}
      />
      {propertyAddress ? (
        <p
          style={{
            marginTop: 'var(--space-4)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            color: 'var(--fg-muted)',
          }}
        >
          {propertyAddress}
        </p>
      ) : null}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CcrUploadPage(): React.JSX.Element {
  const { token = '' } = useParams<{ token: string }>();

  const [view, setView] = useState<CcrUploadView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // --- Initial load. ---
  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadError(null);
    getCcrUpload(token)
      .then((v) => {
        if (!live) return;
        setView(v);
        if (v.state === 'done') setDone(true);
      })
      .catch(() => {
        if (live) setLoadError('We could not load this page. The link may have expired.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [token]);

  const handleUpload = useCallback(async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadCcr(token, file);
      if (alive.current) setDone(true);
    } catch (err) {
      if (!alive.current) return;
      setError(
        err instanceof CcrUploadApiError
          ? (err.errorMessage ?? 'We could not upload that file. Please try again.')
          : 'We could not upload that file. Please try again.',
      );
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [file, token]);

  const shell = useCallback(
    (children: React.ReactNode): React.JSX.Element => (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: 'var(--bg-alt)',
          padding: 'var(--space-7) var(--space-4)',
        }}
      >
        <div style={{ maxWidth: 640, margin: '0 auto' }}>{children}</div>
      </div>
    ),
    [],
  );

  if (loading) {
    return shell(
      <>
        <BrandLockup propertyAddress={null} />
        <p style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>Loading…</p>
      </>,
    );
  }

  if (loadError || !view || view.state === 'unavailable') {
    return shell(
      <>
        <BrandLockup propertyAddress={view?.propertyAddress || null} />
        <div className="alert" data-tone="warning">
          <div className="alert-body">
            <div className="alert-title">This link is no longer available</div>
            {loadError ??
              'This upload link is no longer active. If we still need your CC&Rs, we’ll send a fresh link — or simply reply to our email and a real person will help.'}
          </div>
        </div>
      </>,
    );
  }

  // Already have it (or just uploaded) → "we've got it" state.
  if (done) {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress || null} />
        <div className="card" style={{ textAlign: 'center' }}>
          <h1
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 26,
              fontWeight: 400,
              margin: 0,
              color: 'var(--fg)',
            }}
          >
            Thanks — we&apos;ve got your CC&amp;Rs
          </h1>
          <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
            We&apos;ll take it from here. Questions? Simply reply to our email and a real
            person on our team will be glad to help.
          </p>
        </div>
      </>,
    );
  }

  // --- Open: upload form ---
  return shell(
    <>
      <BrandLockup propertyAddress={view.propertyAddress || null} />

      <div style={{ marginBottom: 'var(--space-6)', textAlign: 'center' }}>
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 26,
            fontWeight: 400,
            margin: 0,
            color: 'var(--fg)',
          }}
        >
          Upload your HOA&apos;s CC&amp;Rs
        </h1>
        <p style={{ marginTop: 'var(--space-3)', color: 'var(--fg-muted)' }}>
          Please share a copy of{' '}
          {view.hoaName ? (
            <>
              <strong style={{ color: 'var(--fg)' }}>{view.hoaName}</strong>&apos;s
            </>
          ) : (
            'your HOA’s'
          )}{' '}
          CC&amp;R document (the recorded covenants, conditions &amp; restrictions) so we
          have it on file. A single PDF is perfect.
        </p>
      </div>

      <div className="card">
        <div className="field" style={{ margin: 0 }}>
          <label htmlFor="ccr-file">CC&amp;R document (PDF)</label>
          <input
            id="ccr-file"
            className="input"
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>

        {error ? (
          <div className="alert" data-tone="danger" style={{ marginTop: 'var(--space-4)' }}>
            <div className="alert-body">{error}</div>
          </div>
        ) : null}

        <div style={{ marginTop: 'var(--space-5)', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn"
            data-size="lg"
            disabled={busy || !file}
            onClick={() => void handleUpload()}
          >
            {busy ? 'Uploading…' : 'Upload CC&Rs'}
          </button>
        </div>
      </div>
    </>,
  );
}
